import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['columnSelect', 'typeSelect', 'labelInput', 'multipleWrapper',
    'defaultValueWrapper', 'datePresetWrapper', 'datePresetSelect', 'datePresetDaysWrapper',
    'datePresetCustomWrapper'];
  static values = { filterTypesMap: Object, datePresetInputs: Object, editing: Boolean, selectedType: String };

  connect() {
    if (!this.hasColumnSelectTarget) return;

    // Track the loaded column so a spurious change event from initializing TomSelect (same value)
    // doesn't clobber a saved label when editing.
    this.currentColumn = this.columnSelectTarget.value;

    // When editing, keep the saved label and type instead of regenerating them from the column.
    this.populateTypeOptions({ regenerateLabel: !this.editingValue });

    if (this.editingValue && this.selectedTypeValue) {
      this.applySelectedType(this.selectedTypeValue);
    }
  }

  columnChanged() {
    // Only regenerate the label when the column actually changed, so editing an existing filter
    // (or any re-fire with the same value) preserves a customized label.
    const columnChanged = this.columnSelectTarget.value !== this.currentColumn;
    this.currentColumn = this.columnSelectTarget.value;
    this.populateTypeOptions({ regenerateLabel: columnChanged });
  }

  populateTypeOptions({ regenerateLabel }) {
    const select = this.columnSelectTarget;
    const selectedOption = select.options[select.selectedIndex];

    if (!selectedOption || !selectedOption.value) {
      this.resetTypeSelect([]);
      return;
    }

    const columnType = selectedOption.dataset.columnType;
    const isForeignKey = selectedOption.dataset.isForeignKey === 'true';
    const isEnum = selectedOption.dataset.isEnum === 'true';

    if (regenerateLabel && this.hasLabelInputTarget && select.value) {
      const columnName = select.value.split('.').pop();
      this.labelInputTarget.value = this.generateLabel(columnName);
    }

    let filterTypes = this.filterTypesMapValue[columnType] || this.filterTypesMapValue['string'] || [];

    if (isEnum) {
      filterTypes = [['Enum', 'RadSearch::EnumFilter']];
    } else if (!isForeignKey) {
      filterTypes = filterTypes.filter(filter => filter[1] !== 'RadSearch::SearchFilter');
    } else {
      filterTypes = this.applySearchScopeOptions(filterTypes, selectedOption);
    }

    this.resetTypeSelect(filterTypes);
  }

  applySelectedType(value) {
    const hasOption = Array.from(this.typeSelectTarget.options).some(option => option.value === value);
    if (hasOption) this.typeSelectTarget.value = value;

    this.updateTypeDependentFields();
  }

  // For a foreign-key column whose target model has global search scopes, replace the plain
  // "Dropdown" option with the scope-driven searchable dropdowns (listed first) followed by
  // "Dropdown (all)" (the original load-everything behaviour, listed last).
  applySearchScopeOptions(filterTypes, selectedOption) {
    const scopes = this.parseSearchScopes(selectedOption);
    if (scopes.length === 0) return filterTypes;

    const scopeOptions = scopes.map(scope => [
      `Dropdown (search by ${scope.description})`,
      `RadSearch::SearchFilter|${scope.name}`
    ]);

    return filterTypes.flatMap(filter => {
      if (filter[1] !== 'RadSearch::SearchFilter') return [filter];
      return [...scopeOptions, ['Dropdown (all)', filter[1]]];
    });
  }

  parseSearchScopes(selectedOption) {
    try {
      return JSON.parse(selectedOption.dataset.searchScopes || '[]');
    } catch {
      return [];
    }
  }

  resetTypeSelect(filterTypes) {
    this.typeSelectTarget.options.length = 0;
    filterTypes.forEach(([label, value]) => this.typeSelectTarget.add(new Option(label, value)));
    this.typeSelectTarget.selectedIndex = 0;
    this.updateTypeDependentFields();
  }

  filterTypeChanged() {
    this.updateTypeDependentFields();
  }

  updateTypeDependentFields() {
    this.updateMultipleVisibility();
    this.updateDatePresetVisibility();
  }

  updateMultipleVisibility() {
    if (!this.hasMultipleWrapperTarget) return;

    const dropdownTypes = ['RadSearch::SearchFilter', 'RadSearch::DistinctValuesFilter'];
    const isDropdown = dropdownTypes.includes(this.selectedBaseType());

    this.multipleWrapperTarget.classList.toggle('d-none', !isDropdown);
  }

  // Date filters use a preset date range as their default instead of a free-text default value.
  // A stale hidden value is harmless: the model drops date_preset for non-date filter types.
  updateDatePresetVisibility() {
    if (!this.hasDatePresetWrapperTarget) return;

    const isDateFilter = this.selectedBaseType() === 'RadSearch::DateFilter';

    this.datePresetWrapperTarget.classList.toggle('d-none', !isDateFilter);
    if (this.hasDefaultValueWrapperTarget) this.defaultValueWrapperTarget.classList.toggle('d-none', isDateFilter);
    this.updateDatePresetInputs();
  }

  datePresetChanged() {
    this.updateDatePresetInputs();
  }

  // "Past Days"/"Next Days" need a day count and "Custom Range" needs start/end dates; show
  // whichever extra inputs the selected preset requires.
  updateDatePresetInputs() {
    if (!this.hasDatePresetSelectTarget) return;

    const inputType = this.datePresetInputsValue[this.datePresetSelectTarget.value];

    if (this.hasDatePresetDaysWrapperTarget) {
      this.datePresetDaysWrapperTarget.classList.toggle('d-none', inputType !== 'days');
    }
    if (this.hasDatePresetCustomWrapperTarget) {
      this.datePresetCustomWrapperTarget.classList.toggle('d-none', inputType !== 'dates');
    }
  }

  selectedBaseType() {
    return this.typeSelectTarget.value.split('|')[0];
  }

  generateLabel(columnName) {
    return columnName.replace(/_/g, ' ').replace(/\b\w/g, (letter) => letter.toUpperCase());
  }
}
